import { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Button,
  Group,
  Stack,
  ColorSwatch,
  Paper
} from '@mantine/core';

const DrawingCanvas = forwardRef(function DrawingCanvas({ disabled, simplifiedDrawing = false, activePowerupEffects = [] }, ref) {
  const canvasRef = useRef(null);
  const bgCanvasRef = useRef(null);
  const currentColorRef = useRef('#000000');
  const lineWidthRef = useRef(3);
  const drawingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const [currentColor, setCurrentColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(3);
  const [activeTool, setActiveTool] = useState('brush'); // 'brush', 'bucket', or 'eraser'
  const activeToolRef = useRef('brush');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const historyRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);

  const saveHistory = useCallback(() => {
    const canvas = canvasRef.current;
    const bgCanvas = bgCanvasRef.current;
    if (!canvas || !bgCanvas) return;
    historyRef.current.push({
      drawing: canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height),
      bg: bgCanvas.getContext('2d').getImageData(0, 0, bgCanvas.width, bgCanvas.height),
    });
    if (historyRef.current.length > 30) historyRef.current.shift();
    setCanUndo(true);
  }, []);

  const undo = useCallback(() => {
    const canvas = canvasRef.current;
    const bgCanvas = bgCanvasRef.current;
    if (!canvas || !bgCanvas || historyRef.current.length === 0) return;
    const entry = historyRef.current.pop();
    canvas.getContext('2d').putImageData(entry.drawing, 0, 0);
    bgCanvas.getContext('2d').putImageData(entry.bg, 0, 0);
    setCanUndo(historyRef.current.length > 0);
  }, []);

  // Keep refs in sync with state for use in native event handlers
  useEffect(() => {
    currentColorRef.current = currentColor;
  }, [currentColor]);
  useEffect(() => { lineWidthRef.current = lineWidth; }, [lineWidth]);
  useEffect(() => { activeToolRef.current = activeTool; }, [activeTool]);

  const floodFill = useCallback((canvas, startX, startY, fillColor) => {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const width = canvas.width;
    const height = canvas.height;

    // Parse fill color
    const temp = document.createElement('canvas').getContext('2d');
    temp.fillStyle = fillColor;
    temp.fillRect(0, 0, 1, 1);
    const [fr, fg, fb] = temp.getImageData(0, 0, 1, 1).data;

    const sx = Math.round(startX);
    const sy = Math.round(startY);
    const idx = (sy * width + sx) * 4;
    const tr = data[idx], tg = data[idx + 1], tb = data[idx + 2], ta = data[idx + 3];

    if (tr === fr && tg === fg && tb === fb) return;

    const tolerance = 30;
    const match = (i) =>
      Math.abs(data[i] - tr) <= tolerance &&
      Math.abs(data[i + 1] - tg) <= tolerance &&
      Math.abs(data[i + 2] - tb) <= tolerance &&
      Math.abs(data[i + 3] - ta) <= tolerance;

    const stack = [[sx, sy]];
    const visited = new Uint8Array(width * height);

    while (stack.length > 0) {
      const [x, y] = stack.pop();
      const pi = y * width + x;
      if (x < 0 || x >= width || y < 0 || y >= height) continue;
      if (visited[pi]) continue;
      const i = pi * 4;
      if (!match(i)) continue;
      visited[pi] = 1;
      data[i] = fr; data[i + 1] = fg; data[i + 2] = fb; data[i + 3] = 255;
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
    ctx.putImageData(imageData, 0, 0);
  }, []);

  const allColors = [
    '#000000', '#FFFFFF', '#FF0000', '#0000FF', '#00FF00',
    '#FF00FF', '#00FFFF', '#FFA500', '#FFC0CB',
    '#8B4513', '#FFD700',
  ];

  // Pick 2 random colors once per mount for simplified mode
  const [simplifiedColors] = useState(() => {
    const pool = allColors.filter(c => c !== '#000000' && c !== '#FFFFFF');
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    return ['#000000', '#FFFFFF', shuffled[0], shuffled[1]];
  });

  // Derive active powerup constraints
  const colorblindEffect = activePowerupEffects.find(e => e.powerupId === 'colorblind');
  const chunkyBrushEffect = activePowerupEffects.find(e => e.powerupId === 'chunky-brush');
  const noEraserEffect = activePowerupEffects.find(e => e.powerupId === 'no-eraser');

  const effectiveColors = colorblindEffect
    ? [colorblindEffect.forcedColor]
    : (simplifiedDrawing ? simplifiedColors : allColors);

  const CHUNKY_BRUSH_SIZE = 32;

  // Enforce chunky-brush: lock to oversized brush
  useEffect(() => {
    if (chunkyBrushEffect) {
      setLineWidth(CHUNKY_BRUSH_SIZE);
      lineWidthRef.current = CHUNKY_BRUSH_SIZE;
      setActiveTool('brush');
      activeToolRef.current = 'brush';
    }
  }, [chunkyBrushEffect]);

  // Enforce colorblind: lock to forced color
  useEffect(() => {
    if (colorblindEffect) {
      setCurrentColor(colorblindEffect.forcedColor);
      currentColorRef.current = colorblindEffect.forcedColor;
    }
  }, [colorblindEffect]);

  // Enforce no-eraser: switch away from eraser if active
  useEffect(() => {
    if (noEraserEffect && activeTool === 'eraser') {
      setActiveTool('brush');
      activeToolRef.current = 'brush';
    }
  }, [noEraserEffect, activeTool]);

  const colors = effectiveColors;

  const bgColors = [
    '#FFFFFF', '#000000', '#FFD700', '#FF6B6B',
    '#74C0FC', '#B2F2BB', '#FFC9DE', '#E5DBFF',
  ];

  // Init canvases
  useEffect(() => {
    const canvas = canvasRef.current;
    const bgCanvas = bgCanvasRef.current;
    if (!canvas || !bgCanvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const bgCtx = bgCanvas.getContext('2d', { willReadFrequently: true });
    bgCtx.fillStyle = 'white';
    bgCtx.fillRect(0, 0, bgCanvas.width, bgCanvas.height);
  }, []);

  // Native touch handlers on canvas — passive: false so preventDefault works
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const t = e.touches[0];
      return {
        x: (t.clientX - rect.left) * (canvas.width / rect.width),
        y: (t.clientY - rect.top) * (canvas.height / rect.height),
      };
    };

    const onTouchStart = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      saveHistory();
      const pos = getPos(e);
      if (activeToolRef.current === 'bucket') {
        floodFill(canvas, pos.x, pos.y, currentColorRef.current);
        return;
      }
      drawingRef.current = true;
      lastPosRef.current = pos;
    };

    const onTouchMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!drawingRef.current || disabled) return;
      const ctx = canvas.getContext('2d');
      const pos = getPos(e);
      const isErasing = activeToolRef.current === 'eraser';
      if (isErasing) {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = 'rgba(0,0,0,1)';
        ctx.lineWidth = lineWidthRef.current;
      } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = currentColorRef.current;
        ctx.lineWidth = lineWidthRef.current;
      }
      ctx.beginPath();
      ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      ctx.globalCompositeOperation = 'source-over';
      lastPosRef.current = pos;
    };

    const onTouchEnd = (e) => {
      e.preventDefault();
      drawingRef.current = false;
    };

    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', onTouchEnd, { passive: false });
    return () => {
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
      canvas.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [disabled, floodFill, saveHistory]);

  // Mouse handlers (for desktop)
  const onMouseDown = (e) => {
    if (disabled) return;
    saveHistory();
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const pos = {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
    if (activeTool === 'bucket') {
      floodFill(canvas, pos.x, pos.y, currentColor);
      return;
    }
    drawingRef.current = true;
    lastPosRef.current = pos;
  };

  const onMouseMove = (e) => {
    if (!drawingRef.current || disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const pos = {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
    const isErasing = activeTool === 'eraser';
    if (isErasing) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = lineWidth;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = currentColor;
      ctx.lineWidth = lineWidth;
    }
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
    lastPosRef.current = pos;
  };

  const onMouseUp = () => { drawingRef.current = false; };

  const fillBackground = (color) => {
    saveHistory();
    setBgColor(color);
    const bgCanvas = bgCanvasRef.current;
    const bgCtx = bgCanvas.getContext('2d');
    bgCtx.fillStyle = color;
    bgCtx.fillRect(0, 0, bgCanvas.width, bgCanvas.height);
  };

  useImperativeHandle(ref, () => ({
    getDrawing() {
      const canvas = canvasRef.current;
      const bgCanvas = bgCanvasRef.current;
      if (!canvas || !bgCanvas) return null;
      const composite = document.createElement('canvas');
      composite.width = canvas.width;
      composite.height = canvas.height;
      const ctx = composite.getContext('2d');
      ctx.drawImage(bgCanvas, 0, 0);
      ctx.drawImage(canvas, 0, 0);
      return composite.toDataURL('image/png');
    },
  }), []);

  const activeEffectBanners = [
    colorblindEffect && { label: `Colorblind — only ${colorblindEffect.forcedColor}`, color: '#FF5722' },
    chunkyBrushEffect && { label: 'Chunky Brush — largest brush only', color: '#9C27B0' },
    noEraserEffect && { label: 'No Eraser — no mistakes allowed!', color: '#F44336' },
  ].filter(Boolean);

  return (
    <Box
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        touchAction: 'none',
      }}
    >
      {/* Powerup effect banners */}
      {activeEffectBanners.map((banner, i) => (
        <Box
          key={i}
          style={{
            flexShrink: 0,
            padding: '4px 12px',
            backgroundColor: banner.color,
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 700,
            textAlign: 'center',
            letterSpacing: '0.04em',
          }}
        >
          <i className="fa-solid fa-bolt" style={{ marginRight: 6 }} />
          {banner.label}
        </Box>
      ))}

      {/* Drawing Tools */}
      <Paper p="sm" radius={0} style={{ flexShrink: 0 }}>
        {simplifiedDrawing ? (
          <Group gap="sm" justify="center" align="center">
            {colors.map(color => (
              <ColorSwatch
                key={color}
                color={color}
                size="1.75rem"
                onClick={() => { setCurrentColor(color); }}
                style={{
                  cursor: 'pointer',
                  border: currentColor === color ? '2px solid #333' : '1px solid #ddd'
                }}
              />
            ))}
            <Button variant="light" color="gray" size="sm" onClick={undo} disabled={!canUndo}>
              <i className="fa-solid fa-rotate-left" />
            </Button>
          </Group>
        ) : (
          <Stack gap="sm">
            <Group gap="xs" justify="center">
              {colors.map(color => (
                <ColorSwatch
                  key={color}
                  color={color}
                  size="1.5rem"
                  onClick={() => { setCurrentColor(color); }}
                  style={{
                    cursor: 'pointer',
                    border: currentColor === color ? '2px solid #333' : '1px solid #ddd'
                  }}
                />
              ))}
            </Group>
            <Group gap="sm" justify="center">
              <Box
                onClick={() => { setActiveTool('brush'); }}
                style={{
                  width: '2.25rem',
                  height: '2.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  border: activeTool === 'brush' ? '2px solid #333' : '2px solid transparent',
                  backgroundColor: activeTool === 'brush' ? '#e9ecef' : 'transparent',
                }}
              >
                <i className="fa-solid fa-paintbrush" />
              </Box>
              <Box
                onClick={() => { setActiveTool('bucket'); }}
                style={{
                  width: '2.25rem',
                  height: '2.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  border: activeTool === 'bucket' ? '2px solid #333' : '2px solid transparent',
                  backgroundColor: activeTool === 'bucket' ? '#e9ecef' : 'transparent',
                }}
              >
                <i className="fa-solid fa-fill-drip" />
              </Box>
              {!noEraserEffect && (
                <Box
                  onClick={() => { setActiveTool('eraser'); }}
                  style={{
                    width: '2.25rem',
                    height: '2.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    fontSize: '1.1rem',
                    border: activeTool === 'eraser' ? '2px solid #333' : '2px solid transparent',
                    backgroundColor: activeTool === 'eraser' ? '#e9ecef' : 'transparent',
                  }}
                >
                  <i className="fa-solid fa-eraser" />
                </Box>
              )}
              {(chunkyBrushEffect
                ? [{ size: CHUNKY_BRUSH_SIZE, diameter: 36 }]
                : [{ size: 2, diameter: 8 }, { size: 4, diameter: 16 }, { size: 8, diameter: 26 }]
              ).map(({ size, diameter }) => (
                <Box
                  key={size}
                  onClick={() => setLineWidth(size)}
                  style={{
                    width: '2.25rem',
                    height: '2.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    border: lineWidth === size ? '2px solid #333' : '2px solid transparent',
                    backgroundColor: lineWidth === size ? '#e9ecef' : 'transparent',
                  }}
                >
                  <Box
                    style={{
                      width: diameter,
                      height: diameter,
                      borderRadius: '50%',
                      backgroundColor: '#000',
                    }}
                  />
                </Box>
              ))}
              <Button variant="light" color="gray" size="sm" onClick={undo} disabled={!canUndo}>
                <i className="fa-solid fa-rotate-left" />
              </Button>
            </Group>
            {!colorblindEffect && (
              <Group gap="xs" justify="center" align="center">
                <i className="fa-solid fa-fill" style={{ fontSize: '0.9rem', color: '#888' }} />
                {bgColors.map(color => (
                  <Box
                    key={`bg-${color}`}
                    onClick={() => fillBackground(color)}
                    style={{
                      width: '1.5rem',
                      height: '1.5rem',
                      backgroundColor: color,
                      cursor: 'pointer',
                      border: bgColor === color ? '2px solid #333' : '1px solid #ccc',
                      borderRadius: 4,
                    }}
                  />
                ))}
              </Group>
            )}
          </Stack>
        )}
      </Paper>

      {/* Canvas — fills remaining space */}
      <Box
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0.5rem',
          touchAction: 'none',
          overflow: 'hidden',
        }}
      >
        <Box style={{ position: 'relative', width: '100%', maxWidth: 'min(90vw, 90vh - 200px)' }}>
          <canvas
            ref={bgCanvasRef}
            width={350}
            height={350}
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
              border: '2px solid #ddd',
              borderRadius: '8px',
              touchAction: 'none',
            }}
          />
          <canvas
            ref={canvasRef}
            width={350}
            height={350}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              display: 'block',
              width: '100%',
              height: '100%',
              borderRadius: '8px',
              touchAction: 'none',
              cursor: disabled ? 'not-allowed' : 'crosshair',
            }}
          />
        </Box>
      </Box>

    </Box>
  );
});

export default DrawingCanvas;
